import { createClient, type QueryParams, SanityClient } from "next-sanity";
import { PHASE_PRODUCTION_BUILD } from "next/constants";

import {
  apiVersion,
  datasetProduction,
  projectId,
  resolveDataset,
} from "./env";

const tagMaxlength = 75; // Sanity query tag max length is 75 characters
const truncatedSuffix = ".truncated";
const cleanSanityQueryTag = (tag: string): string => {
  if (tag.length > tagMaxlength) {
    return (
      tag.substring(0, tagMaxlength - truncatedSuffix.length) + truncatedSuffix
    );
  }

  return tag;
};

function createRequestTagPrefix(
  isSanityPreview: boolean = false,
  projectTag: string,
) {
  const isBuilding = process.env.PHASE === PHASE_PRODUCTION_BUILD;
  const isVercelPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === "preview";
  const isProd = resolveDataset() === datasetProduction;
  const isDev = process.env.NODE_ENV === "development";

  return cleanSanityQueryTag(
    [
      // Build
      isBuilding ? "b" : false,
      // Sanity Preview
      isSanityPreview ? "sp" : false,
      // Environment
      isProd ? "pr" : false,
      isDev ? "d" : false,
      isVercelPreview ? "vp" : false,
      // Project
      projectTag,
    ]
      .filter(Boolean)
      .join("-"),
  );
}

// Don't use the CDN when:
// - Building on Vercel or Muster.
// - In development.
let useCdn: boolean = true;

if (process.env.VERCEL_BUILD === "1" || process.env.CI === "true") {
  useCdn = false;
} else if (process.env.NODE_ENV === "development") {
  useCdn = false;
}

const config = {
  projectId,
  dataset: resolveDataset(),
  apiVersion,
  useCdn,
};

type TaggedFetch = (
  query: string,
  params?: QueryParams,
  options?: { tag?: string },
) => Promise<unknown>;

/**
 * Wraps a Sanity client with a function that cleans the query tags.
 * @param client - The Sanity client to wrap.
 * @returns The wrapped Sanity client.
 */
function withCleanQueryTags(client: SanityClient): SanityClient {
  const boundFetch: TaggedFetch = client.fetch.bind(client);
  client.fetch = ((query, params, options = {}) => {
    const { tag, ...rest } = options;
    return boundFetch(query, params, {
      ...rest,
      ...(tag != null ? { tag: cleanSanityQueryTag(tag) } : {}),
    });
  }) satisfies TaggedFetch as SanityClient["fetch"];
  return client;
}

const clientCache = new Map<string, SanityClient>();

export const getClient = (
  projectTag: string,
  isPreview: boolean,
): SanityClient => {
  const cacheKey = `${projectTag}:${isPreview}`;
  const cached = clientCache.get(cacheKey);
  if (cached) return cached;

  const instance = withCleanQueryTags(
    isPreview
      ? createClient({
          ...config,
          perspective: "drafts",
          token: process.env.SANITY_API_READ_TOKEN,
          useCdn: false, // Don't use CDN when previewing the drafts.
          requestTagPrefix: createRequestTagPrefix(true, projectTag),
        })
      : createClient({
          ...config,
          requestTagPrefix: createRequestTagPrefix(false, projectTag),
        }),
  );

  clientCache.set(cacheKey, instance);
  return instance;
};
